/**
 * The seven open alerts on the OEM+ Alerts page.
 *
 * Source: `const ALERTS=[ … ]` at line 2245 of
 * `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html` (7 records).
 *
 * Porting decisions
 * - `machine` was written as `IDS.T22` / `IDS.J07` / `IDS.L31` in the demo.
 *   `IDS` is `B.ids` (line 1836), a display-id indirection map:
 *   `L14→L-02, J07→J-04, T22→T-09, L31→L-13, L03→L-04, L22→L-06, S05→S-05,
 *    L18→L-18, S04→S-04, S09→S-09, L20→L-20`. Those references are resolved to
 *   their literal machine ids here (`'T-09'`, `'J-04'`, `'L-13'`), which is what
 *   the demo renders anyway.
 * - Positional tuples became named-field interfaces, named from the renderer
 *   `alertPage()` (line ~2253):
 *     `evidence: [source, detail, ref]`  → `AlertEvidence { source, detail, ref }`
 *     `chain:    [agent, output, status, label, approvalKind?]`
 *                                        → `AlertChainStep`
 * - `evidence[2]` is an overloaded string. It is modelled as the discriminated
 *   union `AlertEvidenceRef`, mirroring the branches in `alertPage()`:
 *     `'doc:…' | 'eml:N' | 'x:…'` → `{ kind: 'document', file }` (openDoc)
 *     `'plan'`                    → `{ kind: 'drillPlan', file: 'plan:drillplan' }`
 *     `'eml'` (bare)              → `{ kind: 'emailThread', file: 'eml:11' }`
 *     anything else               → `{ kind: 'dataset', dataset }` (source-rows drawer)
 *   Each variant keeps the original token in `raw` so nothing is lost.
 * - STRIPPED MARKUP: chain `output` strings contained `<b>…</b>` around a figure
 *   or identifier inside the sentence (e.g. `<b>0.91</b>`, `<b>WO-26-11912</b>`,
 *   `<b>empty haul</b>`). The tags are stripped and the text kept verbatim; the
 *   bolded substrings are listed in the optional `emphasis` field so a scene can
 *   re-apply the emphasis without re-parsing HTML. No other markup was present.
 * - DROPPED FIELD: every record ended `clip:null`. No consumer reads `.clip`
 *   anywhere in the demo, so it is not carried over.
 *   TODO(P3): if a video clip per alert is ever wanted, re-add it as a typed
 *   optional field — the demo never populated it.
 * - No handler strings: the alert page's `show('machine', …)` /
 *   `show('alerts', id)` calls live in the renderer, not in this constant.
 * - `sev`, `mode`, chain `status` and `approvalKind` are closed sets, so they are
 *   derived union types rather than `string`.
 *
 * Data Schema layer — pure data, no behaviour.
 */

/** Alert severity. `'crit'` renders "Critical alert", `'warn'` renders "Watch". */
export type AlertSeverity = 'crit' | 'warn';

/** Where the alert fired: inside the shift on live data, or off a shift-end read. */
export type AlertMode = 'LIVE' | 'SHIFT-END';

/** Whether a chain step is finished or is waiting on a person. */
export type AlertChainStatus = 'done' | 'approve';

/** What an `'approve'` chain step commits when a person presses Approve. */
export type AlertApprovalKind = 'wo' | 'so' | 'order' | 'restrict';

/** Where a piece of evidence can be opened. */
export type AlertEvidenceRef =
  /** A table in the store; the demo renders a "rows" drill-down for it. */
  | { readonly kind: 'dataset'; readonly dataset: string; readonly raw: string }
  /** A file opened with `openDoc()` — a procedure, bulletin, email or extra file. */
  | { readonly kind: 'document'; readonly file: string; readonly raw: string }
  /** The IREDES drill plan; the demo hard-codes `openDoc('plan:drillplan')`. */
  | { readonly kind: 'drillPlan'; readonly file: 'plan:drillplan'; readonly raw: string }
  /** A bare `'eml'` token; the demo hard-codes `openDoc('eml:11')`. */
  | { readonly kind: 'emailThread'; readonly file: 'eml:11'; readonly raw: string };

/** One "Evidence pulled in" chip. */
export interface AlertEvidence {
  /** System or role the evidence came from, bolded in the chip, e.g. `'SAP PM'`. */
  readonly source: string;
  /** What it says, e.g. `'PM-500 28 Aug: pads 82%'`. */
  readonly detail: string;
  /** Where the chip's "open" button goes. */
  readonly ref: AlertEvidenceRef;
}

/** One row of the "Agent chain" for an alert. */
export interface AlertChainStep {
  /** Agent that ran the step, e.g. `'Parts & logistics'`. */
  readonly agent: string;
  /** What it produced, markup stripped. */
  readonly output: string;
  /** `'done'` or `'approve'` (waiting on a person). */
  readonly status: AlertChainStatus;
  /** Pill text, e.g. `'Ran 04:41'` or `'Needs approval'`. */
  readonly label: string;
  /** Present only on `'approve'` steps; drives the toast on approval. */
  readonly approvalKind?: AlertApprovalKind;
  /** Substrings that were `<b>` in the source string, in order of appearance. */
  readonly emphasis?: readonly string[];
}

/** One open alert. */
export interface Alert {
  /** Alert id, e.g. `'AL-0917'`; also the `show('alerts', id)` argument. */
  readonly id: string;
  /** Machine id the alert is against, e.g. `'T-09'`. */
  readonly machine: string;
  /** Component, matched against the condition register by name. */
  readonly comp: string;
  readonly sev: AlertSeverity;
  readonly mode: AlertMode;
  /** When it was raised, as displayed: `'09 Sep 04:41'`. */
  readonly raised: string;
  /** Headline. */
  readonly title: string;
  /** The action window, printed large next to the confidence. */
  readonly window: string;
  /** Model confidence, 0–1; the demo prints `Math.round(conf*100)`. */
  readonly conf: number;
  /** Short label used on the overview KPI chips; falls back to `comp.toLowerCase()`. */
  readonly short?: string;
  /** The reasoning paragraph. */
  readonly why: string;
  readonly evidence: readonly AlertEvidence[];
  readonly chain: readonly AlertChainStep[];
}

const ds = (dataset: string): AlertEvidenceRef => ({ kind: 'dataset', dataset, raw: dataset });
const doc = (file: string): AlertEvidenceRef => ({ kind: 'document', file, raw: file });

/** The seven open alerts, in demo order. */
export const ALERTS: readonly Alert[] = [
  {
    id: 'AL-0917',
    machine: 'T-09',
    comp: 'Retarder & service brakes',
    sev: 'crit',
    mode: 'LIVE',
    raised: '09 Sep 04:41',
    title: 'Rear brake temperature exceedances on 1:7 decline',
    window: 'Restrict now · inspect at 10:00 crib',
    conf: 0.91,
    why: 'Rear brake temperature above 240 °C on 4 of the last 6 loaded descents from 1,450 level; retarder duty 71%; speed 16 km/h against a 14 km/h decline limit; pads measured 82% worn on 28 Aug. Live MQTT feed, so the alert fired inside the shift.',
    evidence: [
      { source: 'Live feed', detail: 'brake_temp_rr_c · 10 s samples', ref: ds('live') },
      { source: 'SAP PM', detail: 'PM-500 28 Aug: pads 82%', ref: ds('wos') },
      {
        source: 'Procedure',
        detail: 'SP-MT63-BRK-002 §1 trigger: 4 events >220 °C',
        ref: doc('doc:SP-MT63-BRK-002_RevA_Rear-Brake-and-Retarder-Inspection.docx'),
      },
    ],
    chain: [
      {
        agent: 'Diagnostic',
        output: 'Matched 4 exceedances in shift to the SP-MT63-BRK-002 trigger; pads at 82% from PM-500. 0.91.',
        status: 'done',
        label: 'Ran 04:41',
        emphasis: ['0.91'],
      },
      {
        agent: 'Knowledge',
        output: 'Attached SP-MT63-BRK-002 §2 steps, IR cool-down warning, decline operating note.',
        status: 'done',
        label: 'Attached',
      },
      {
        agent: 'Parts & logistics',
        output: 'MI store holds 3 × MER-BRK-PAD-63; reserved one set. No order.',
        status: 'done',
        label: 'Reserved',
        emphasis: ['3'],
      },
      {
        agent: 'Work order',
        output: 'Drafted WO-26-11912 · Anders Lindqvist · 10:00 crib · 5 h.',
        status: 'approve',
        label: 'Needs approval',
        approvalKind: 'wo',
        emphasis: ['WO-26-11912'],
      },
      {
        agent: 'Safety & compliance',
        output: 'Restricted to empty haul until inspected; LOTO-UG-003 wheel work → 2nd-person verify.',
        status: 'approve',
        label: 'Needs approval',
        approvalKind: 'restrict',
        emphasis: ['empty haul'],
      },
    ],
  },
  {
    id: 'AL-0912',
    machine: 'J-04',
    comp: 'RH rock drill',
    sev: 'crit',
    mode: 'SHIFT-END',
    raised: '08 Sep 07:12',
    title: 'Rock drill front-end failure predicted',
    window: '60–110 percussion h · 4–7 days at rehab duty',
    conf: 0.79,
    why: 'Percussion pressure at full collar down 8% over nine days (TB-2026-027 signature), accumulator pre-charge drifting, shank adapter 30 h past change interval, water leak at the front cover confirmed by the technician at the cut change. No kit in NSW — that is the real problem.',
    evidence: [
      { source: 'Pitram', detail: 'percussion_pressure daily mean, 29 Aug – 8 Sep', ref: ds('metric') },
      {
        source: 'Drill plan',
        detail: '1840XC cut 212: RH holes 176 bar vs LH 194',
        ref: { kind: 'drillPlan', file: 'plan:drillplan', raw: 'plan' },
      },
      {
        source: 'Bulletin',
        detail: 'TB-2026-027 §2 signature',
        ref: doc('doc:TB-2026-027_Rock-Drill-Percussion-Pressure-Decay-Precursor.docx'),
      },
      {
        source: 'Technician',
        detail: 'Ben Tuilagi cut-change inspection 22:40 — water at front cover, one-sided shank wear',
        ref: { kind: 'emailThread', file: 'eml:11', raw: 'eml' },
      },
    ],
    chain: [
      {
        agent: 'Diagnostic',
        output: '−8% percussion trend + pre-charge + shank overdue → 0.79, 60–110 percussion h.',
        status: 'done',
        label: 'Ran 07:12',
        emphasis: ['0.79'],
      },
      {
        agent: 'Knowledge',
        output: 'SP-MJ2D-RD-011 Rev C steps + 420 N·m torques + accumulator discharge warning.',
        status: 'done',
        label: 'Attached',
      },
      {
        agent: 'Parts & logistics',
        output: 'BR store 0 · Newcastle DC 0 · Perth DC 3. Air 3 d ($520) vs road 6 d. Drafted SO-90418.',
        status: 'approve',
        label: 'Needs approval',
        approvalKind: 'so',
        emphasis: ['SO-90418'],
      },
      {
        agent: 'Work order',
        output: 'Drafted WO-26-11901 for next cut change · Ben Tuilagi · 6 h.',
        status: 'approve',
        label: 'Needs approval',
        approvalKind: 'wo',
        emphasis: ['WO-26-11901'],
      },
      {
        agent: 'Safety & compliance',
        output: 'Percussion accumulator discharge step made mandatory; boom stands required.',
        status: 'done',
        label: 'Cleared 07:13',
      },
    ],
  },
  {
    id: 'AL-0915',
    machine: 'L-13',
    comp: 'Engine',
    sev: 'warn',
    mode: 'LIVE',
    raised: '08 Sep 15:20',
    title: 'Engine derate events — intake air temperature, tele-remote LHD',
    window: 'Watch · check air filter at next crib',
    conf: 0.68,
    why: 'Three derate events today on the 1,450 level when intake air exceeded 48 °C; camera 2 dropouts on the same level point at Wi-Fi handover rather than the machine. Two different problems that look the same to an operator sitting on surface.',
    evidence: [
      { source: 'Live feed', detail: 'intake_air_c, engine_derate_pct', ref: ds('live') },
      { source: 'Case', detail: 'CS-2026-2188 camera dropouts (Wi-Fi AP)', ref: ds('cases') },
      { source: 'Shift-end', detail: 'alarm counts trending up 4 days', ref: ds('shift') },
    ],
    chain: [
      {
        agent: 'Diagnostic',
        output: 'Separated derate (machine) from video dropout (network). 0.68.',
        status: 'done',
        label: 'Ran 15:20',
        emphasis: ['0.68'],
      },
      {
        agent: 'Knowledge',
        output: 'PM-MER LHD 250 h air filter item; remote module check.',
        status: 'done',
        label: 'Attached',
      },
      {
        agent: 'Parts & logistics',
        output: 'Air filter in MI store (engine service kit).',
        status: 'done',
        label: 'In stock',
      },
      {
        agent: 'Work order',
        output: 'Added to next PM-250 (due 11 Sep) rather than a new WO.',
        status: 'done',
        label: 'Merged',
      },
      {
        agent: 'Safety & compliance',
        output: 'Tele-remote receiver key step flagged for the PM.',
        status: 'done',
        label: 'Cleared',
      },
    ],
  },
  // ---- three more alerts — a battery loader (shift-end BMS log), and two surface drills on the RMS live feed
  {
    id: 'AL-0919',
    machine: 'L-07',
    comp: 'Battery pack (LFP, 300 kWh)',
    sev: 'crit',
    mode: 'SHIFT-END',
    raised: '06 Sep 18:20',
    title: 'Battery module isolation fault — pack derated, fast charge locked out',
    window: 'Single-shift duty · module fitted when SO-90427 lands (09 Sep PM)',
    conf: 0.94,
    short: 'battery isolation',
    why: 'Module 7 isolation resistance dropped below the BMS threshold at 14:48 (E-BAT-ISO in the BMS log read at shift end). The BMS derated the pack to 60% and locked out fast charge. The 9 °C module temperature spread on fast charge on 04 Sep was the precursor; there are no MER-BAT-MOD modules east of Townsville.',
    evidence: [
      { source: 'BMS log', detail: 'E-BAT-ISO 14:48 · W-BAT-DERATE 07/08 Sep', ref: ds('events') },
      {
        source: 'Condition register',
        detail: 'Battery pack 18/100 · thermal management 52/100',
        ref: ds('health'),
      },
      {
        source: 'Salesforce',
        detail: 'CS-2026-2216 warranty claim under assessment · SO-90427 air from Townsville',
        ref: ds('cases'),
      },
      { source: 'Outlook', detail: 'L-07 thread — Marco Bellini / Lena Sjoberg', ref: doc('eml:11') },
    ],
    chain: [
      {
        agent: 'Diagnostic',
        output: 'Isolation fault on one module with a prior thermal-spread precursor → module replacement, not pack. 0.94.',
        status: 'done',
        label: 'Ran 18:20',
        emphasis: ['0.94'],
      },
      {
        agent: 'Knowledge',
        output: 'ML-21E HV isolation procedure and the fast-charge lockout rule attached; Ben Tuilagi HV-current (LMS).',
        status: 'done',
        label: 'Attached',
      },
      {
        agent: 'Parts & logistics',
        output: 'BR store 0 · Newcastle 0 · Townsville 1 → SO-90427 air, ETA 09 Sep PM.',
        status: 'done',
        label: 'Ordered',
        emphasis: ['SO-90427'],
      },
      {
        agent: 'Work order',
        output: 'Drafted WO-26-11878, approved by Helen Kaur 07 Sep 07:20.',
        status: 'done',
        label: 'Approved',
        emphasis: ['WO-26-11878'],
      },
      {
        agent: 'Safety & compliance',
        output: 'Single-shift duty, no override of the charge lockout; HV isolation test after fit.',
        status: 'done',
        label: 'In force',
      },
    ],
  },
  {
    id: 'AL-0925',
    machine: 'L-33',
    comp: 'Charging interface',
    sev: 'crit',
    mode: 'SHIFT-END',
    raised: '09 Sep 05:12',
    title: 'Charge handshake failing after wash-down — connector moisture, battery loader',
    window: '3–5 charge cycles · connector change Thursday (Hamish carries the part)',
    conf: 0.74,
    short: 'charge connector',
    why: "Three of the last five charge connections at bay 2 failed the handshake (E-CHG-HANDSHAKE in the BMS log read at shift end) and the fault followed the loader to bay 1 on Tuesday, so it is the vehicle-side connector. The insulation resistance the BMS logs at each connection has fallen from 210 to 74 MΩ since Saturday's wash-down — moisture behind the connector seal (TB-2026-034 signature). The loader started two shifts below 40% state of charge. No connector on site; Burnie service centre holds one.",
    evidence: [
      { source: 'BMS log', detail: 'E-CHG-HANDSHAKE ×3 · W-SOC-LOW 08 Sep', ref: ds('events') },
      {
        source: 'Condition register',
        detail: 'Charging interface 24/100 · battery thermal management 47/100 (L-30)',
        ref: ds('health'),
      },
      {
        source: 'Salesforce',
        detail: 'CS-2026-2223 predictive alert · SO-90435 draft requisition',
        ref: ds('cases'),
      },
      {
        source: 'Outlook',
        detail: 'L-33 thread — Declan Hurst / Zoe Lindgren / Ruth Kellaway',
        ref: doc('eml:22'),
      },
      {
        source: 'Remote support',
        detail: 'XS-2052 — connector face inspected on camera, moisture track between pins 3 and 4',
        ref: doc('x:XS-2052_Session-Report_L-33_Mount-Read-Metals_2026-09-09.pdf'),
      },
    ],
    chain: [
      {
        agent: 'Diagnostic',
        output: 'Handshake failures follow the machine, insulation falling after a wash-down → connector moisture, not the charger. 0.74, fails within 3–5 cycles.',
        status: 'done',
        label: 'Ran 05:12',
        emphasis: ['0.74'],
      },
      {
        agent: 'Knowledge',
        output: 'SP-ML21E-CHG-003 (connector replacement, HV two-point lockout) and the TB-2026-034 dry-wipe card attached; KB-0143 from the remote session.',
        status: 'done',
        label: 'Attached',
      },
      {
        agent: 'Parts & logistics',
        output: 'MR store 0 · Burnie service centre 1 → SO-90435 drafted; recommend Hamish Cameron carries it on Thursday rather than Bass Strait freight (3 d).',
        status: 'approve',
        label: 'Needs approval',
        approvalKind: 'order',
        emphasis: ['SO-90435'],
      },
      {
        agent: 'Work order',
        output: 'Drafted WO-26-11917 · Hamish Cameron (only HV-authorised Meridian tech in TAS) · Thu 10 Sep · 2.5 h.',
        status: 'approve',
        label: 'Needs approval',
        approvalKind: 'wo',
        emphasis: ['WO-26-11917'],
      },
      {
        agent: 'Safety & compliance',
        output: 'Bay 2 locked out per SB-2026-005; L-33 on bay 1 only until the connector is changed; charging interface off the wash-down route (site card issued).',
        status: 'done',
        label: 'In force',
      },
    ],
  },
  {
    id: 'AL-0921',
    machine: 'S-04',
    comp: 'DTH hammer & pipes',
    sev: 'crit',
    mode: 'LIVE',
    raised: '08 Sep 11:30',
    title: 'DTH hammer piston wear — penetration rate down 31% at constant air',
    window: '40–70 drilling h · exchange at the pattern change (Thu)',
    conf: 0.83,
    short: 'hammer wear',
    why: 'Penetration rate on S-04 has fallen 31% over eight days while air pressure, rotation and feed force stayed flat and S-03 on the same bench did not change — that is the hammer, not the ground. The RMS feed over site LTE carries the per-hole numbers, so the alert fired inside the shift. One exchange hammer in the Ochre Hill store, reserved.',
    evidence: [
      { source: 'RMS feed', detail: 'penetration_m_h daily mean, 29 Aug – 8 Sep', ref: ds('metric') },
      { source: 'RMS roll-up', detail: 'drilling h, metres, air bar per day', ref: ds('rmsd') },
      {
        source: 'Procedure',
        detail: 'SP-MS120-DTH-021 §3 signature and trigger',
        ref: doc('doc:SP-MS120-DTH-021_RevB_DTH-Hammer-Exchange-and-Drill-String-Inspection.docx'),
      },
      { source: 'Outlook', detail: 'Mei Lin Tan → Kieran Walsh, 08 Sep 12:15', ref: doc('eml:21') },
    ],
    chain: [
      {
        agent: 'Diagnostic',
        output: '−31% penetration at constant air/rotation, peer rig flat → hammer piston/bit wear, 40–70 h. 0.83.',
        status: 'done',
        label: 'Ran 11:30',
        emphasis: ['0.83'],
      },
      {
        agent: 'Knowledge',
        output: 'SP-MS120-DTH-021 exchange steps, 8.5 kN·m make-up, thread-gauge every pipe while the string is out (SP-MS-PIPE-007).',
        status: 'done',
        label: 'Attached',
      },
      {
        agent: 'Parts & logistics',
        output: 'OH store 1 × MER-DTH-HMR-165 → reserved; replenishment from Darwin drafted (SO-90430).',
        status: 'done',
        label: 'Reserved',
        emphasis: ['SO-90430'],
      },
      {
        agent: 'Work order',
        output: 'Drafted WO-26-11913 · Kieran Walsh · pattern change Thursday · 4 h.',
        status: 'approve',
        label: 'Needs approval',
        approvalKind: 'wo',
        emphasis: ['WO-26-11913'],
      },
      {
        agent: 'Safety & compliance',
        output: 'Blow-down before breaking any joint; table clear while the head is powered.',
        status: 'done',
        label: 'Cleared 11:31',
      },
    ],
  },
  {
    id: 'AL-0923',
    machine: 'S-09',
    comp: 'Rotary head',
    sev: 'crit',
    mode: 'LIVE',
    raised: '09 Sep 03:15',
    title: 'Rotary head motor wear — torque oscillation and case-drain flow',
    window: 'Reduce feed force now · motor lands Thursday (SO-90431)',
    conf: 0.81,
    short: 'rotary head',
    why: 'Rotation torque on S-09 is swinging ±40% at steady feed and gets worse with hole depth; the rotary head motor case-drain flow is 2.6× its baseline and copper is up in the hydraulic oil sample. Live RMS feed from the Stage 3 cutback, so the agent could act at 03:15. No exchange motor at Gawler Deep; one at Adelaide DC.',
    evidence: [
      {
        source: 'RMS live',
        detail: 'rotation_torque_knm, rot_motor_case_drain_lpm · 1-min samples',
        ref: ds('rms'),
      },
      { source: 'Oil analysis', detail: 'Hydraulic sample 08 Sep — Cu 88 ppm, Caution', ref: ds('oil') },
      { source: 'Condition register', detail: 'Rotary head 35/100 · very poor', ref: ds('health') },
      { source: 'Outlook', detail: 'Callum Byrne → Noah Petrov / Deng Achol, 09 Sep 04:10', ref: doc('eml:20') },
    ],
    chain: [
      {
        agent: 'Diagnostic',
        output: 'Torque oscillation + case-drain 2.6× + copper in oil → rotary head motor wear. 0.81.',
        status: 'done',
        label: 'Ran 03:15',
        emphasis: ['0.81'],
      },
      {
        agent: 'Knowledge',
        output: 'Rotary head motor exchange steps and seal kit; feed-force reduction note for the operator.',
        status: 'done',
        label: 'Attached',
      },
      {
        agent: 'Parts & logistics',
        output: 'GD store 0 · Adelaide DC 1 → drafted SO-90431, road 2 d, with the L-17 hose kits.',
        status: 'approve',
        label: 'Needs approval',
        approvalKind: 'so',
        emphasis: ['SO-90431'],
      },
      {
        agent: 'Work order',
        output: 'Drafted WO-26-11914 · Noah Petrov · 5 h.',
        status: 'approve',
        label: 'Needs approval',
        approvalKind: 'wo',
        emphasis: ['WO-26-11914'],
      },
      {
        agent: 'Safety & compliance',
        output: 'Rig moved to the pre-split row with feed force backed off — no restriction beyond that.',
        status: 'done',
        label: 'Cleared 03:16',
      },
    ],
  },
];

/** The alert the Alerts page opens on (`let ALERT_CUR=ALERTS[0].id`). */
export const DEFAULT_ALERT_ID: string = 'AL-0917';

/**
 * Toast copy the demo shows after approving a chain step, keyed by
 * `AlertApprovalKind` (from `approve()` at line ~2292). `'order'` has no entry
 * in the demo map, so it falls through to the generic `'Approved'`.
 */
export const ALERT_APPROVAL_TOASTS: Readonly<Record<AlertApprovalKind, string>> = {
  wo: 'Work order approved and scheduled in SAP PM',
  so: 'Requisition sent to Salesforce parts portal · air freight booked',
  restrict: 'Restriction pushed to Pitram/FMS: empty-haul only',
  order: 'Approved',
};
